import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  api,
  desdeISO,
  type CanalConocido,
  type Causas,
  type SaludHttp,
  type CanalResumen,
  type Vital,
  esSesionVencida,
  VENTANAS,
  type Ejemplo,
  type Embudo as EmbudoDatos,
  type ProcesoResumen,
  type Servicio,
  type Sesion,
  type SpanTraza,
} from './api';
import { Acceso } from './componentes/Acceso';
import { DescargarIcono } from './componentes/DescargarIcono';
import { OjoIcono } from './componentes/OjoIcono';
import { Tabla } from './componentes/Tabla';
import { FondoAcceso } from './componentes/FondoAcceso';
import { PieVoxcom } from './componentes/PieVoxcom';
import { Alertas } from './componentes/Alertas';
import { Analisis } from './componentes/Analisis';
import { PanelAnomalias } from './componentes/Anomalias';
import { Auditoria } from './componentes/Auditoria';
import { compararCanales, etiquetaDeCanal } from './canales';
import { fechaYHora } from './fechas';
import { avisoDeFuente, type Fuente } from './fuente';
import { CambiarContrasena } from './componentes/CambiarContrasena';
import { ComparativaCanales } from './componentes/Canales';
import { Cascada } from './componentes/Cascada';
import { CAUSAS } from './causas';
import { BuscadorGlobal, type Pagina } from './componentes/BuscadorGlobal';
import { Icono } from './componentes/Icono';
import { Resumen } from './componentes/Resumen';
import { TemaIcono } from './componentes/TemaIcono';
import { TrazaModal } from './componentes/TrazaModal';
import { alternar, aplicarTema, guardarPreferencia, leerPreferencia, resolverTema } from './tema';
import { alternarLateral, aplicarLateral, guardarLateral, iniciales, leerLateral } from './lateral';
import { Embudo } from './componentes/Embudo';
import { Equipo } from './componentes/Equipo';
import { MenuConfiguracion } from './componentes/MenuConfiguracion';
import { ActualizarIcono } from './componentes/ActualizarIcono';
import { CampanaAlertas } from './componentes/CampanaAlertas';
import { BandaFacturacion, CuentaSuspendida } from './componentes/AvisoFacturacion';
import { Rendimiento } from './componentes/Rendimiento';
import { AceptarInvitacion } from './componentes/Invitacion';
import { Recuperar } from './componentes/Recuperar';
import { Releases } from './componentes/Releases';
import { Versiones } from './componentes/Versiones';
import { AdministrarSegundoFactor, PedirSegundoFactor } from './componentes/SegundoFactor';
import { SinDatos } from './componentes/SinDatos';
import { Restablecer } from './componentes/Restablecer';

type Pestana =
  | 'resumen'
  | 'procesos'
  | 'analisis'
  | 'releases'
  | 'servicios'
  | 'auditoria'
  | 'equipo'
  | 'alertas'
  | 'versiones';
type PantallaPublica =
  | 'acceso'
  | 'recuperar'
  | 'restablecer'
  | 'segundo_factor'
  | 'invitacion';

/**
 * El enlace del correo llega a /restablecer?token=... El portal devuelve el
 * index para cualquier ruta, así que basta con mirar la URL: no hace falta un
 * enrutador para dos pantallas.
 */
function pantallaDeLaUrl(): { pantalla: PantallaPublica; token: string } {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token') ?? '';
  if (url.pathname === '/restablecer' && token) return { pantalla: 'restablecer', token };
  if (url.pathname === '/invitacion' && token) return { pantalla: 'invitacion', token };
  return { pantalla: 'acceso', token: '' };
}

/** Limpia el token de la barra de direcciones para que no quede en el historial. */
function limpiarUrl(): void {
  window.history.replaceState({}, '', '/');
}

/** Cómo se llama cada resultado en pantalla, cuando no es el mismo que se guarda. */
const ETIQUETA_RESULTADO: Record<string, string> = { BUSINESS_ERROR: 'NEGOCIO' };

export function Ejemplos({
  ejemplos,
  filtro,
  onFiltrar,
  onAbrir,
  trazaAbierta,
  spans,
  puedeVerTrazas,
  sinEjemplos,
}: {
  ejemplos: Ejemplo[];
  filtro: string;
  onFiltrar: (valor: string) => void;
  onAbrir: (traceId: string) => void;
  trazaAbierta: string | null;
  /** Los spans de la traza abierta. Vacío mientras se piden. */
  spans: SpanTraza[];
  puedeVerTrazas: boolean;
  /** Motivo por el que no hay ejecuciones que abrir, si lo hay. */
  sinEjemplos: string | null;
}) {
  return (
    <section className="ejemplos">
      <div className="ejemplos-cabecera">
        <h3>Ejecuciones</h3>
        <div className="filtros">
          {['', 'OK', 'ERROR', 'BUSINESS_ERROR', 'ABANDONED'].map((valor) => (
            <button
              key={valor || 'todas'}
              className={filtro === valor ? 'activo' : ''}
              onClick={() => onFiltrar(valor)}
            >
              {valor === '' ? 'Todas' : (ETIQUETA_RESULTADO[valor] ?? valor)}
            </button>
          ))}
        </div>
      </div>

      {ejemplos.length === 0 ? (
        <p className="vacio">{sinEjemplos ?? 'Sin ejecuciones con ese filtro.'}</p>
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Resultado</th>
              <th>Causa</th>
              <th>Canal</th>
              <th>Plataforma</th>
              <th>Versión</th>
              <th>Motivo</th>
              <th className="num">Duración</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ejemplos.map((e) => {
              const abierto = trazaAbierta === e.trace_id;
              return (
                // Fragmento y no una sola fila: el detalle es su propia `tr`
                // justo debajo de la suya. Antes se dibujaba después de la
                // tabla entera, y con veinte ejecuciones había que buscar
                // dónde había quedado lo que uno acababa de abrir.
                <Fragment key={e.trace_id}>
                  <tr className={abierto ? 'seleccionada' : ''}>
                    <td>{fechaYHora(e.ts)}</td>
                    <td>
                      <span className={`resultado ${e.result.toLowerCase()}`}>
                        {ETIQUETA_RESULTADO[e.result] ?? e.result}
                      </span>
                    </td>
                    <td>
                      {e.causa ? (
                        <>
                          <span className={`causa ${e.causa}`} title={CAUSAS[e.causa].explicacion}>
                            {CAUSAS[e.causa].etiqueta}
                          </span>
                          {e.llamada && (
                            <span className="causa-llamada" title={e.llamada}>
                              {e.llamada} · {e.codigo || 'sin respuesta'}
                            </span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={`canal ${e.canal}`}>{etiquetaDeCanal(e.canal)}</span>
                    </td>
                    <td>{e.platform || '—'}</td>
                    <td>{e.app_version || '—'}</td>
                    <td>{e.desenlace ? e.desenlace.replace(/_/g, ' ') : '—'}</td>
                    <td className="num">{Math.round(e.duration_ms)} ms</td>
                    <td className="acciones-fila">
                      {puedeVerTrazas && (
                        // Un enlace y no un botón con fetch: el portal responde con
                        // `attachment` y deja anotada la exportación en la bitácora.
                        <a
                          className="icono-ojo"
                          href={`/informe/traza.md?traza=${encodeURIComponent(e.trace_id)}`}
                          aria-label="Descargar el detalle de llamadas (Markdown)"
                          title="Descargar el detalle de llamadas (Markdown)"
                        >
                          <DescargarIcono />
                        </a>
                      )}
                      {puedeVerTrazas && (
                        <button
                          type="button"
                          className="icono-ojo"
                          onClick={() => onAbrir(e.trace_id)}
                          aria-expanded={abierto}
                          // El icono solo no dice qué hace: el nombre
                          // accesible y el tooltip sí, y son la misma frase.
                          aria-label={abierto ? 'Esconder la traza' : 'Ver la traza'}
                          title={abierto ? 'Esconder la traza' : 'Ver la traza'}
                        >
                          <OjoIcono abierto={abierto} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {abierto && (
                    <tr className="detalle-traza">
                      <td colSpan={9}>
                        <div className="traza">
                          <h4>
                            Traza <code>{e.trace_id.slice(0, 16)}…</code>
                          </h4>
                          {spans.length === 0 ? (
                            <p className="vacio">Cargando la traza…</p>
                          ) : (
                            <Cascada spans={spans} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Tabla>
      )}
    </section>
  );
}

export function App() {
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [comprobando, setComprobando] = useState(true);
  const [inicial] = useState(pantallaDeLaUrl);
  const [pantalla, setPantalla] = useState<PantallaPublica>(inicial.pantalla);

  const [ventana, setVentana] = useState(60);
  const [pestana, setPestana] = useState<Pestana>('resumen');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [trazaModal, setTrazaModal] = useState<string | null>(null);
  const [nombresDeProcesos, setNombresDeProcesos] = useState<string[]>([]);
  const [preferenciaDeTema, setPreferenciaDeTema] = useState(() => leerPreferencia());
  const [sistemaEsClaro, setSistemaEsClaro] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches
  );
  const tema = resolverTema(preferenciaDeTema, sistemaEsClaro);

  // El tema sigue al sistema mientras la persona no elija uno. Al elegir, deja
  // de seguirlo: quien cambia a mano quiere algo distinto de lo que le da el equipo.
  useEffect(() => {
    aplicarTema(tema);
  }, [tema]);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const consulta = matchMedia('(prefers-color-scheme: light)');
    const alCambiar = (e: MediaQueryListEvent) => setSistemaEsClaro(e.matches);
    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, []);
  const alternarTema = useCallback(() => {
    const nuevo = alternar(tema);
    setPreferenciaDeTema(nuevo);
    guardarPreferencia(nuevo);
  }, [tema]);
  const [lateral, setLateral] = useState(() => leerLateral());
  useEffect(() => {
    aplicarLateral(lateral);
  }, [lateral]);
  const alternarMenuLateral = useCallback(() => {
    const nuevo = alternarLateral(lateral);
    setLateral(nuevo);
    guardarLateral(nuevo);
  }, [lateral]);
  const cerrarTrazaModal = useCallback(() => setTrazaModal(null), []);

  const [procesos, setProcesos] = useState<ProcesoResumen[]>([]);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [embudo, setEmbudo] = useState<EmbudoDatos | null>(null);
  const [ejemplos, setEjemplos] = useState<Ejemplo[]>([]);
  const [fuente, setFuente] = useState<Fuente>('crudo');
  const [sinEjemplos, setSinEjemplos] = useState<string | null>(null);
  const [causas, setCausas] = useState<Causas | null>(null);
  const [filtro, setFiltro] = useState('');
  const [canal, setCanal] = useState('');
  const [canalesDisponibles, setCanalesDisponibles] = useState<CanalConocido[]>([]);
  const [comparativa, setComparativa] = useState<CanalResumen[]>([]);
  const [trazaAbierta, setTrazaAbierta] = useState<string | null>(null);
  const [spans, setSpans] = useState<SpanTraza[]>([]);
  const [rendimiento, setRendimiento] = useState<{
    http?: SaludHttp[];
    servicios: Servicio[];
    vitals: Vital[];
    advertencia?: string;
    fuente?: string;
  } | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cambiandoContrasena, setCambiandoContrasena] = useState(false);
  // null = todavía no se preguntó; { ultimo: null } = el tenant nunca envió nada.
  const [ultimoDato, setUltimoDato] = useState<{ ultimo: string | null } | null>(null);

  // Al cargar la página se le pregunta al portal si hay sesión viva. El estado
  // de autenticación nunca se deduce de algo guardado en el navegador.
  /**
   * El alto real del encabezado, publicado como variable CSS.
   *
   * La lista de procesos se queda fija mientras se baja por el detalle, y para
   * eso necesita saber donde termina el encabezado —que es `sticky` y le
   * pasaria por encima—. El numero no se escribe a mano porque no es
   * constante: la cabecera envuelve en pantallas angostas y cambia de alto, y
   * un valor fijo dejaria la lista tapada o flotando con un hueco.
   */
  useEffect(() => {
    const cabecera = document.querySelector('.app .barra');
    if (!cabecera) return;
    const publicar = () => {
      document.documentElement.style.setProperty(
        '--alto-cabecera',
        `${Math.round(cabecera.getBoundingClientRect().height)}px`
      );
    };
    publicar();
    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(publicar);
    observador.observe(cabecera);
    return () => observador.disconnect();
  }, []);

  useEffect(() => {
    let vigente = true;
    api
      .sesion()
      .then((s) => vigente && setSesion(s))
      .catch(() => undefined)
      .finally(() => vigente && setComprobando(false));
    return () => {
      vigente = false;
    };
  }, []);

  const limpiarVistas = useCallback(() => {
    setProcesos([]);
    setFuente('crudo');
    setSinEjemplos(null);
    setSeleccionado(null);
    setEmbudo(null);
    setEjemplos([]);
    setCausas(null);
    setRendimiento(null);
    setTrazaAbierta(null);
    setSpans([]);
  }, []);

  /** Una sesión vencida devuelve al acceso en vez de dejar la pantalla rota. */
  const manejarError = useCallback(
    (err: unknown) => {
      if (esSesionVencida(err)) {
        setSesion(null);
        limpiarVistas();
        return;
      }
      setError(err instanceof Error ? err.message : 'error desconocido');
    },
    [limpiarVistas]
  );

  /**
   * Los canales del filtro del encabezado.
   *
   * Se piden aparte y no dentro del cargador de cada pestaña. Antes los ponía
   * únicamente el camino de Procesos —y solo si había un proceso
   * seleccionado—, así que entrando directo a Rendimiento el filtro no
   * aparecía hasta que se pasaba por Procesos y volvía.
   *
   * Y se piden sin proceso: los que traía ese camino eran los canales de un
   * proceso, no los del cliente. Un filtro que está en el encabezado se aplica
   * a todas las pestañas, así que su lista tiene que ser la de todas.
   */
  useEffect(() => {
    if (!sesion) return;
    let vigente = true;
    api
      .canales(desdeISO(ventana))
      .then(
        (d) =>
          vigente &&
          setCanalesDisponibles(
            [...d.disponibles].sort((a, b) => compararCanales(a.canal, b.canal))
          )
      )
      // Sin ruido: si esto falla, lo que se pierde es el filtro, y el cargador
      // de la pestaña ya va a mostrar el error si el problema es de verdad.
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, [sesion, ventana]);

  /**
   * Cuántas veces se pidió recargar la vista.
   *
   * Las anomalías no se piden con el resto —su consulta no lleva parámetros y
   * vive en su propio componente— así que necesitan una señal para saber que
   * la vista se recargó. Sin esto se piden una sola vez al montar: si el
   * problema se resuelve con el panel abierto, la tarjeta sigue ahí, no porque
   * el sistema crea que sigue pasando sino porque nadie volvió a preguntar.
   * Justo cuando alguien acaba de arreglar la incidencia y viene a mirar.
   */
  const [recargas, setRecargas] = useState(0);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setRecargas((n) => n + 1);
    setCargando(true);
    setError(null);
    const desde = desdeISO(ventana);

    try {
      // La auditoría y el equipo se cargan solos en sus componentes: tienen
      // sus propios filtros y no comparten estado con la vista de procesos.
      if (
        pestana === 'resumen' ||
        pestana === 'auditoria' ||
        pestana === 'equipo' ||
        pestana === 'alertas' ||
        pestana === 'releases' ||
        pestana === 'analisis' ||
        pestana === 'versiones'
      )
        return;

      if (pestana === 'servicios') {
        const [datosServicios, datosVitals] = await Promise.all([
          api.servicios(desde, canal || undefined),
          api.vitals(desde, canal || undefined),
        ]);
        setRendimiento({ ...datosServicios, vitals: datosVitals.vitals });
        return;
      }

      const { procesos: lista, fuente: origen } = await api.procesos(desde, canal || undefined);
      setProcesos(lista);
      setFuente(origen);

      // Solo se pregunta cuando hace falta explicar un vacío: en el camino
      // normal no se gasta una consulta extra.
      if (lista.length === 0) setUltimoDato(await api.ultimo());
      else setUltimoDato(null);

      const activo = seleccionado ?? lista[0]?.process_name ?? null;
      if (activo !== seleccionado) setSeleccionado(activo);
      if (!activo) {
        setEmbudo(null);
        setEjemplos([]);
        setCausas(null);
        return;
      }

      const [datosEmbudo, datosEjemplos, datosCausas, datosCanales] = await Promise.all([
        api.embudo(activo, desde, canal || undefined),
        api.ejemplos(activo, desde, filtro || undefined, canal || undefined),
        // Es un complemento: si falla —por ejemplo, el portal ya se actualizó y
        // la API de consulta todavía no— se pierde el reparto de la tarjeta de
        // errores y no la pantalla entera.
        api.causas(activo, desde, canal || undefined).catch(() => ({ causas: null })),
        // La comparativa se pide sin filtrar por canal: su gracia es mostrar
        // todos los canales lado a lado, incluso cuando hay uno seleccionado.
        api.canales(desde, activo),
      ]);
      setEmbudo(datosEmbudo);
      setEjemplos(datosEjemplos.ejemplos);
      setSinEjemplos(datosEjemplos.sin_ejemplos);
      setCausas(datosCausas.causas);
      setComparativa(datosCanales.comparativa);
    } catch (err) {
      manejarError(err);
    } finally {
      setCargando(false);
    }
  }, [sesion, ventana, pestana, seleccionado, filtro, canal, manejarError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const abrirTraza = useCallback(
    async (traceId: string) => {
      if (trazaAbierta === traceId) {
        setTrazaAbierta(null);
        setSpans([]);
        return;
      }
      setTrazaAbierta(traceId);
      try {
        const { spans: datos } = await api.traza(traceId);
        setSpans(datos);
      } catch (err) {
        manejarError(err);
      }
    },
    [trazaAbierta, manejarError]
  );

  const cambiarTenant = useCallback(
    async (tenantId: string) => {
      try {
        const nueva = await api.cambiarTenant(tenantId);
        limpiarVistas();
        // El rol puede ser distinto en el otro tenant: si pierde el acceso a
        // servicios, se vuelve a la pestaña que sí puede ver.
        if (!nueva.permisos.puedeVerServicios || !nueva.permisos.puedeAdministrar) {
          // El rol puede ser distinto en el otro tenant: si pierde el acceso a
          // una pestaña, se vuelve a la que sí puede ver.
          setPestana('procesos');
        }
        setSesion(nueva);
      } catch (err) {
        manejarError(err);
      }
    },
    [limpiarVistas, manejarError]
  );

  const salir = useCallback(async () => {
    try {
      await api.salir();
    } finally {
      setSesion(null);
      limpiarVistas();
    }
  }, [limpiarVistas]);

  if (comprobando) return <p className="cargando">Cargando…</p>;

  // La invitación se muestra con o sin sesión: quien ya tiene cuenta necesita
  // estar dentro justamente para poder aceptarla.
  if (pantalla === 'invitacion') {
    return (
      <AceptarInvitacion
        token={inicial.token}
        onListo={() => {
          limpiarUrl();
          setPantalla('acceso');
          void api.sesion().then(setSesion).catch(() => setSesion(null));
        }}
      />
    );
  }

  if (!sesion) {
    const fuera = (contenido: React.ReactNode) => (
      <>
        <FondoAcceso />
        {contenido}
      </>
    );

    if (pantalla === 'restablecer') {
      return fuera(
        <Restablecer
          token={inicial.token}
          onListo={() => {
            limpiarUrl();
            setPantalla('acceso');
          }}
        />
      );
    }
    if (pantalla === 'recuperar') {
      return fuera(<Recuperar onVolver={() => setPantalla('acceso')} />);
    }
    if (pantalla === 'segundo_factor') {
      return fuera(
        <PedirSegundoFactor
          onListo={(s) => {
            setPantalla('acceso');
            setSesion(s);
          }}
          onCancelar={() => setPantalla('acceso')}
        />
      );
    }
    return fuera(
      <Acceso
        onEntrar={setSesion}
        onSegundoFactor={() => setPantalla('segundo_factor')}
        onRecuperar={() => setPantalla('recuperar')}
      />
    );
  }

  const { permisos, tenant, tenants, usuario } = sesion;

  /** Las páginas del menú y del buscador: una sola lista para que no diverjan. */
  const paginas: Array<Pagina & { id: Pestana }> = [
    { id: 'resumen', etiqueta: 'Resumen', icono: 'resumen' },
    { id: 'procesos', etiqueta: 'Procesos', icono: 'procesos' },
    // El informe nombra servicios internos: mismo alcance que Rendimiento.
    ...(permisos.puedeVerServicios
      ? ([{ id: 'analisis', etiqueta: 'Análisis', icono: 'analisis' }] as const)
      : []),
    ...(permisos.puedeVerServicios
      ? ([{ id: 'servicios', etiqueta: 'Rendimiento', icono: 'rendimiento' }] as const)
      : []),
    // Comparar releases es una vista de negocio: la ve cualquier rol, igual
    // que el embudo del que salen las mismas métricas.
    { id: 'releases', etiqueta: 'Releases', icono: 'releases' },
    // Qué corre y qué reporta cada canal: versiones de la plataforma y del
    // SDK, no datos del cliente. La ve cualquier rol.
    { id: 'versiones', etiqueta: 'Versiones', icono: 'versiones' },
    ...(permisos.puedeAdministrar
      ? ([{ id: 'auditoria', etiqueta: 'Auditoría', icono: 'auditoria' }] as const)
      : []),
  ];

  function irAPagina(id: string): void {
    setPestana(id as Pestana);
    setMenuAbierto(false);
  }

  function irAProceso(proceso: string): void {
    setPestana('procesos');
    setSeleccionado(proceso);
    setTrazaAbierta(null);
    setSpans([]);
    setMenuAbierto(false);
  }

  /** Los nombres de proceso para el buscador: se piden al enfocarlo, una vez. */
  function cargarNombresDeProcesos(): void {
    if (nombresDeProcesos.length > 0) return;
    api
      .procesos(desdeISO(60 * 24 * 7))
      .then((r) => setNombresDeProcesos(r.procesos.map((p) => p.process_name)))
      // Sin ruido: si falla, el buscador sigue encontrando páginas y trazas.
      .catch(() => undefined);
  }

  return (
    <>
      <FondoAcceso variante="panel" />
      <div className={`app ${menuAbierto ? 'menu-abierto' : ''}`}>
      <nav className="lateral" aria-label="Navegación principal">
        <div className="lateral-cabecera">
          <div className="lateral-marca">
            <strong>
              Vox<span>Observability</span>
            </strong>
            <small>
              Un producto de <i className="lateral-logo" role="img" aria-label="Voxcom" />
            </small>
          </div>
          <span className="lateral-mono" aria-hidden="true">
            V
          </span>
          <button type="button" className="icono lateral-cerrar" aria-label="Cerrar el menú" onClick={() => setMenuAbierto(false)}>
            <Icono nombre="cerrar" />
          </button>
          <button
            type="button"
            className="icono lateral-alternar"
            onClick={alternarMenuLateral}
            aria-label={lateral === 'compacto' ? 'Expandir el menú' : 'Contraer el menú'}
            aria-pressed={lateral === 'compacto'}
            title={lateral === 'compacto' ? 'Expandir el menú' : 'Contraer el menú'}
          >
            <Icono nombre={lateral === 'compacto' ? 'expandir' : 'contraer'} />
          </button>
        </div>

        <div className="lateral-tenant">
          {/* Compacto no tiene sitio para el nombre ni para el selector: queda
              una ficha con las iniciales, y tocarla expande el menú, que es
              donde se puede elegir. */}
          <button
            type="button"
            className="tenant-ficha"
            onClick={alternarMenuLateral}
            title={`${tenant.nombre}: expandir el menú`}
            aria-label={`Cliente ${tenant.nombre}. Expandir el menú`}
          >
            {iniciales(tenant.nombre)}
          </button>
          {tenants.length > 1 ? (
            <select
              className="selector-tenant"
              value={tenant.id}
              aria-label="Cliente"
              onChange={(e) => void cambiarTenant(e.target.value)}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          ) : (
            <span className="tenant">{tenant.nombre}</span>
          )}
        </div>

        <ul className="lateral-lista">
          {paginas.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={pestana === p.id ? 'activo' : ''}
                aria-current={pestana === p.id ? 'page' : undefined}
                aria-label={p.etiqueta}
                title={p.etiqueta}
                onClick={() => irAPagina(p.id)}
              >
                <Icono nombre={p.icono} />
                <span className="lateral-texto">{p.etiqueta}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="lateral-pie">
          <div className="lateral-usuario">
            <span className="avatar" aria-hidden="true">
              {usuario.nombre
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase())
                .join('') || '?'}
            </span>
            <div>
              <strong>{usuario.nombre}</strong>
              <small>{tenant.rol}</small>
            </div>
          </div>
          <button type="button" className="salir" aria-label="Salir" title="Salir" onClick={() => void salir()}>
            <Icono nombre="salir" />
            <span className="lateral-texto">Salir</span>
          </button>
        </div>
      </nav>
      <div className="lateral-velo" onClick={() => setMenuAbierto(false)} aria-hidden="true" />

      <div className="cuerpo">
      <header className="barra">
        <button type="button" className="icono menu-boton" aria-label="Abrir el menú" onClick={() => setMenuAbierto(true)}>
          <Icono nombre="menu" />
        </button>

        <BuscadorGlobal
          paginas={paginas}
          procesos={nombresDeProcesos}
          puedeVerTrazas={permisos.puedeVerServicios}
          alCargarProcesos={cargarNombresDeProcesos}
          onPagina={irAPagina}
          onProceso={irAProceso}
          onTraza={setTrazaModal}
        />

        <div className="controles">
          {canalesDisponibles.length > 1 && (
            <select
              value={canal}
              onChange={(e) => setCanal(e.target.value)}
              title="Filtrar por canal"
              aria-label="Filtrar por canal"
            >
              <option value="">Todos los canales</option>
              {canalesDisponibles.map((c) => (
                <option key={c.canal} value={c.canal}>
                  {etiquetaDeCanal(c.canal)}
                  {/* Un canal conocido que no se movio en la ventana se
                      muestra igual, dicho: que desaparezca del listado hace
                      que «se cayo» se lea como «nunca existio». */}
                  {c.conTrafico ? '' : ' — sin tráfico'}
                </option>
              ))}
            </select>
          )}
          <select value={ventana} onChange={(e) => setVentana(Number(e.target.value))} aria-label="Ventana de tiempo">
            {VENTANAS.map((v) => (
              <option key={v.minutos} value={v.minutos}>
                {v.etiqueta}
              </option>
            ))}
          </select>
          <button type="button" className="actualizar" onClick={() => void cargar()} disabled={cargando} aria-label="Actualizar">
            <ActualizarIcono />
            <span>{cargando ? 'Cargando…' : 'Actualizar'}</span>
          </button>
          <button
            type="button"
            className="icono tema-boton"
            onClick={alternarTema}
            aria-label={tema === 'claro' ? 'Cambiar al tema oscuro' : 'Cambiar al tema claro'}
            title={tema === 'claro' ? 'Tema oscuro' : 'Tema claro'}
          >
            <TemaIcono haciaClaro={tema === 'oscuro'} />
          </button>
          <CampanaAlertas
            recarga={recargas}
            onVerProceso={(proceso) => irAProceso(proceso)}
          />
          <MenuConfiguracion
            puedeAdministrar={permisos.puedeAdministrar}
            seccionActiva={pestana === 'equipo' || pestana === 'alertas' ? pestana : null}
            onMiCuenta={() => setCambiandoContrasena(true)}
            onSeccion={(seccion) => setPestana(seccion)}
          />
        </div>
      </header>

      {trazaModal && (
        <TrazaModal
          traceId={trazaModal}
          puedeDescargar={permisos.puedeVerServicios}
          onCerrar={cerrarTrazaModal}
          onError={manejarError}
        />
      )}

      {cambiandoContrasena && (
        <CambiarContrasena
          onCerrar={() => setCambiandoContrasena(false)}
          segundoFactor={
            <AdministrarSegundoFactor
              activo={usuario.segundoFactor}
              codigosRestantes={usuario.codigosRespaldoRestantes}
              onCambio={() => void api.sesion().then(setSesion).catch(() => undefined)}
            />
          }
        />
      )}

      {/* El 402 de un tenant suspendido ya tiene su propia pantalla más abajo:
          mostrar además el error genérico de la primera consulta que falló
          diría lo mismo dos veces con menos contexto la primera vez. */}
      {error && tenant.facturacion?.estado !== 'suspendido' && (
        <p className="error banda">{error}</p>
      )}
      {tenant.facturacion && <BandaFacturacion facturacion={tenant.facturacion} />}

      {/*
        Se dice antes de que el usuario mire los números, no en una nota al
        pie. Comparar un p95 aproximado contra uno exacto sin saberlo es la
        forma de salir a buscar una regresión que no ocurrió.
      */}
      {(() => {
        const aviso = pestana === 'procesos' ? avisoDeFuente(fuente) : null;
        return aviso ? (
          <p className="banda agregado">
            <strong>{aviso.titulo}.</strong> {aviso.detalle}
          </p>
        ) : null;
      })()}

      {tenant.facturacion?.estado === 'suspendido' ? (
        <CuentaSuspendida />
      ) : pestana === 'resumen' ? (
        <main className="una-columna">
          <Resumen
            ventana={ventana}
            canal={canal}
            recarga={recargas}
            puedeVerServicios={permisos.puedeVerServicios}
            onVentana={setVentana}
            onVerProceso={irAProceso}
            onIrA={(p) => irAPagina(p)}
            onAbrirTraza={setTrazaModal}
            onError={manejarError}
          />
        </main>
      ) : pestana === 'analisis' ? (
        <main className="una-columna">
          <Analisis desde={desdeISO(ventana)} onError={manejarError} />
        </main>
      ) : pestana === 'releases' ? (
        <main className="una-columna">
          <Releases desde={desdeISO(ventana)} canal={canal} onError={manejarError} />
        </main>
      ) : pestana === 'versiones' ? (
        <main className="una-columna">
          <Versiones onError={manejarError} />
        </main>
      ) : pestana === 'alertas' ? (
        <main className="una-columna">
          <Alertas onError={manejarError} />
        </main>
      ) : pestana === 'equipo' ? (
        <main className="una-columna">
          <Equipo onError={manejarError} />
        </main>
      ) : pestana === 'auditoria' ? (
        <main className="una-columna">
          <Auditoria desde={desdeISO(ventana)} onError={manejarError} />
        </main>
      ) : pestana === 'servicios' ? (
        <main className="una-columna">
          {rendimiento && (
            <Rendimiento
              vitals={rendimiento.vitals}
              servicios={rendimiento.servicios}
              {...(rendimiento.http ? { http: rendimiento.http } : {})}
              {...(rendimiento.advertencia ? { advertencia: rendimiento.advertencia } : {})}
              {...(rendimiento.fuente ? { fuente: rendimiento.fuente } : {})}
              desde={desdeISO(ventana)}
              {...(canal ? { canal } : {})}
              trazaAbierta={trazaAbierta}
              spans={spans}
              onAbrirTraza={(id) => void abrirTraza(id)}
            />
          )}
        </main>
      ) : (
        <main>
          <aside>
            <h2>Procesos</h2>
            {procesos.length === 0 &&
              (ultimoDato ? (
                <SinDatos
                  ultimo={ultimoDato.ultimo}
                  ventanaActual={ventana}
                  onAmpliar={(minutos) => setVentana(minutos)}
                />
              ) : (
                <p className="vacio">Buscando…</p>
              ))}
            <ul>
              {procesos.map((p) => {
                const total = Number(p.ejecuciones);
                const exito = total > 0 ? Math.round((Number(p.ok) / total) * 100) : 0;
                return (
                  <li key={p.process_name}>
                    <button
                      className={seleccionado === p.process_name ? 'activo' : ''}
                      onClick={() => {
                        setSeleccionado(p.process_name);
                        setTrazaAbierta(null);
                        setSpans([]);
                      }}
                    >
                      <span className="proceso-nombre">{p.process_name}</span>
                      <span className="proceso-meta">
                        {total} ejec · {exito}% éxito · p95 {p.p95_ms} ms
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="detalle">
            <PanelAnomalias
              recarga={recargas}
              onVerProceso={(proceso) => {
                setSeleccionado(proceso);
                setTrazaAbierta(null);
                setSpans([]);
              }}
            />
            <ComparativaCanales canales={comparativa} seleccionado={canal} onSeleccionar={setCanal} />
            {embudo && <Embudo datos={embudo} causas={causas} />}

            {seleccionado && (
              <Ejemplos
                ejemplos={ejemplos}
                filtro={filtro}
                onFiltrar={setFiltro}
                onAbrir={(id) => void abrirTraza(id)}
                trazaAbierta={trazaAbierta}
                spans={spans}
                puedeVerTrazas={permisos.puedeVerServicios}
                sinEjemplos={sinEjemplos}
              />
            )}
          </div>
        </main>
      )}

      <PieVoxcom />
      </div>
      </div>
    </>
  );
}
